import type { TargetAndTransition, Transition } from "motion/react";
import { UI_MOTION } from "../../config/uiMotion.configuration";
import {
    resolveUiEaseOut,
    resolveUiSpring,
    resolveUiStagger,
} from "../shared/motion/uiMotion.tools";

/** Stable presence key for the portaled list panel. */
export const MINI_MODAL_SELECT_LIST_PRESENCE_KEY = "mini-modal-select-list";

/**
 * Builds the transform origin for the list panel unfold animation.
 *
 * @param opensBelow Whether the panel opens below the trigger.
 * @returns CSS transform-origin value.
 */
export const resolveMiniModalSelectListTransformOrigin = ( opensBelow: boolean ): string => {
    return opensBelow ? "top center" : "bottom center";
};

/**
 * Enter / idle motion for the portaled list shell.
 *
 * @returns Motion target for the open state.
 */
export const buildMiniModalSelectListOpenState = (): TargetAndTransition => {
    return {
        "opacity": 1,
        "scale":   1,
        "y":       0,
        "filter":  "blur(0px)",
    };
};

/**
 * Initial motion state for the portaled list shell.
 *
 * @param opensBelow Whether the panel opens below the trigger.
 * @returns Motion initial target.
 */
export const buildMiniModalSelectListClosedState = ( opensBelow: boolean ): TargetAndTransition => {
    return {
        "opacity": 0,
        "scale":   0.94,
        "y":       opensBelow ? -6 : 6,
        "filter":  "blur(4px)",
    };
};

/**
 * Exit motion state for the portaled list shell.
 *
 * @param opensBelow Whether the panel opens below the trigger.
 * @returns Motion exit target.
 */
export const buildMiniModalSelectListExitState = ( opensBelow: boolean ): TargetAndTransition => {
    return {
        "opacity": 0,
        "scale":   0.97,
        "y":       opensBelow ? -4 : 4,
        "filter":  "blur(2px)",
    };
};

/**
 * Spring transition for the list panel open animation.
 *
 * @param reducedMotion Whether the user prefers reduced motion.
 * @returns Motion transition.
 */
export const buildMiniModalSelectListOpenTransition = ( reducedMotion: boolean ): Transition => {
    return resolveUiSpring( reducedMotion, "gentle" );
};

/**
 * Ease-out transition for the list panel dismiss animation.
 *
 * @param reducedMotion Whether the user prefers reduced motion.
 * @returns Motion transition.
 */
export const buildMiniModalSelectListExitTransition = ( reducedMotion: boolean ): Transition => {
    return resolveUiEaseOut( reducedMotion, UI_MOTION.duration.fastMs );
};

/**
 * Staggered reveal transition for a list option row.
 *
 * @param index Zero-based option index.
 * @param reducedMotion Whether the user prefers reduced motion.
 * @returns Motion transition for the row reveal.
 */
export const buildMiniModalSelectOptionRevealTransition = (
    index: number,
    reducedMotion: boolean,
): Transition => {
    return {
        ...resolveUiSpring( reducedMotion, "gentle" ),
        "delay": resolveUiStagger( index, reducedMotion ),
    };
};

/**
 * Initial motion state for a list option row reveal.
 *
 * @returns Motion initial target.
 */
export const buildMiniModalSelectOptionInitialState = (): TargetAndTransition => {
    return {
        "opacity": 0,
        "x":       -10,
    };
};

/**
 * Motion target for a revealed list option row.
 *
 * @returns Motion animate target.
 */
export const buildMiniModalSelectOptionVisibleState = (): TargetAndTransition => {
    return {
        "opacity": 1,
        "x":       0,
    };
};

/**
 * Transition used when the trigger label changes after a new selection.
 *
 * @param reducedMotion Whether the user prefers reduced motion.
 * @returns Motion transition.
 */
export const buildMiniModalSelectTriggerLabelTransition = ( reducedMotion: boolean ): Transition => {
    return resolveUiEaseOut( reducedMotion, 160 );
};

/**
 * Chevron rotation transition for the trigger affordance.
 *
 * @param reducedMotion Whether the user prefers reduced motion.
 * @returns Motion transition.
 */
export const buildMiniModalSelectChevronTransition = ( reducedMotion: boolean ): Transition => {
    return resolveUiSpring( reducedMotion, "default" );
};
